"use client";

import { useMemoizedFn } from "ahooks";
import { useHandleImportClick } from "./useHandleImportClick";
import { openPortfolioAccountsModal } from "@components/PlaidImportModal";
import type { PlaidAccountCategory } from "@components/PlaidConnectFlow";
import type {
  PlaidUploadEntry,
  PlaidUploadFrom,
} from "@components/PlaidConnectFlow/types";
import type { PlaidLinkTrackingContext } from "@/src/hooks/plaid/plaidLinkTracking";
import type { PlaidBindSuccessContext } from "@components/PlaidImportModal/types";
import { sleep } from "@utils/sleep";
import { OnboardingPaywallFreeTrialContentProps } from "@components/OnboardingWizard/Paywall/types";
import type { SenderAttachment } from "@components/Sender/type";
import { Track } from "@/src/lib/tracking";

export interface UseAccountConnectionFlowOptions {
  /** Runs after built-in folder refresh on Plaid bind success. */
  onBindSuccess?: (ctx: PlaidBindSuccessContext) => void | Promise<void>;
  /** MANUAL_UPLOAD Confirm 回调：附件已上传完成，由调用方处理 */
  onUploadSuccess?: (attachments: SenderAttachment[]) => void | Promise<void>;
  /** Runs when user closes the Plaid import modal. */
  onComplete?: () => void;
  /** 关闭回调 */
  onClose?: () => void;
  paywallCopyOverrides?: OnboardingPaywallFreeTrialContentProps["paywallCopyOverrides"];
  // 允许自动打开管理弹窗
  autoOpenManageModal?: boolean;
  /** 账户类型 (invest / bank) */
  accountType?: PlaidAccountCategory;
  /** 是否来自 Onboarding 流程 */
  fromOnboarding?: boolean;
  /** 是否已选择/关联 Portfolio (用于退出挽留分流) */
  hasSelectedPortfolio?: boolean;
  /** 指定直连的 Broker ID */
  initialBrokerId?: number;
  /** 授权过期重新连接时的 account_id (仅 update mode 需要) */
  accountId?: string;
  /**
   * 当已在账户管理页面时，Plaid 绑定成功后跳过确认弹窗。
   * MyAccountsPage 等场景设为 true，避免重复弹出账户列表。
   */
  skipAccountsConfirm?: boolean;
}

export interface StartFlowParams {
  accountType?: PlaidAccountCategory;
  fromOnboarding?: boolean;
  hasSelectedPortfolio?: boolean;
  initialBrokerId?: number;
  accountId?: string;
  connectionId?: string;
  onClose?: () => void;
  /** EDG-561 重叠分析链路：以下 5 项原样透传给 `handleImportClick`，缺省时行为不变 */
  skipPaywall?: boolean;
  suppressAccountReadPending?: boolean;
  uploadEntry?: PlaidUploadEntry;
  uploadFrom?: PlaidUploadFrom;
  /** 透传 `plaid_link_*` 业务维度（如 Chat 大类卡的 `entry`，EDG-591） */
  plaidTrackingContext?: PlaidLinkTrackingContext;
}

export interface UseAccountConnectionFlowReturn {
  /** Ghost state / connect hero entry — routes to manage modal or fresh connect. */
  startFlow: (params?: StartFlowParams) => Promise<void>;
  /** Manage button entry — opens accounts modal, wires the add-account CTA. */
  startManageFlow: () => Promise<void>;
  /** Runs when user taps Add Account from manage views. */
  openAddAccount?: () => void | Promise<void>;
}

/**
 * Orchestrates Plaid connect + manage modal flow.
 *
 * Flow graph:
 *   startFlow → handleImportClick
 *   startManageFlow → openPortfolioAccountsModal
 *       └─ onAddAccount → handleImportClick({ forceFreshConnect: true })
 */
export const useAccountConnectionFlow = (
  options?: UseAccountConnectionFlowOptions,
): UseAccountConnectionFlowReturn => {
  const {
    onBindSuccess,
    onUploadSuccess,
    onComplete,
    onClose,
    paywallCopyOverrides,
    autoOpenManageModal = true,
    accountType,
    fromOnboarding,
    hasSelectedPortfolio,
    initialBrokerId,
    accountId,
    skipAccountsConfirm = false,
  } = options || {};

  const accountTarget =
    accountType === "bank"
      ? "banking"
      : accountType === "invest"
        ? "invest"
        : undefined;

  const { handleImportClick } = useHandleImportClick({
    onBindSuccess: async (ctx) => {
      await sleep(2000);
      await onBindSuccess?.(ctx);
      if (!autoOpenManageModal) {
        return;
      }
      await openPortfolioAccountsModal({
        onBindSuccess,
        onAddAccount: openAddAccount,
        confirmEnabled: true,
        accountTarget,
      });
    },
    onComplete,
    onClose,
    paywallCopyOverrides,
    accountType,
    fromOnboarding,
    hasSelectedPortfolio,
    initialBrokerId,
    accountId,
    onUploadSuccess,
  });

  const openAddAccount = useMemoizedFn(async () => {
    await handleImportClick({
      forceFreshConnect: true,
      requireAccountsConfirm: !skipAccountsConfirm,
      accountTarget,
      accountType,
      fromOnboarding,
      hasSelectedPortfolio,
      initialBrokerId,
      accountId,
      onClose,
    });
  });

  const startManageFlow = useMemoizedFn(async () => {
    try {
      const result = await openPortfolioAccountsModal({
        onBindSuccess,
        onAddAccount: openAddAccount,
        closeOnAddAccount: true,
        accountTarget,
      });
      if (result.action === "add_account") {
        await openAddAccount();
      }
    } catch (error) {
      Track.error(error, {
        tag: "useAccountConnectionFlow",
        msg: "manage flow error:",
      });
    }
  });

  const startFlow = useMemoizedFn(async (params?: StartFlowParams) => {
    if (
      !params &&
      !accountType &&
      !fromOnboarding &&
      !hasSelectedPortfolio &&
      !initialBrokerId &&
      !accountId &&
      !onClose
    ) {
      await handleImportClick();
      return;
    }
    await handleImportClick({
      accountType: params?.accountType ?? accountType,
      fromOnboarding: params?.fromOnboarding ?? fromOnboarding,
      hasSelectedPortfolio:
        params?.hasSelectedPortfolio ?? hasSelectedPortfolio,
      initialBrokerId: params?.initialBrokerId ?? initialBrokerId,
      accountId: params?.accountId ?? accountId,
      connectionId: params?.connectionId,
      onClose: params?.onClose ?? onClose,
      skipPaywall: params?.skipPaywall,
      suppressAccountReadPending: params?.suppressAccountReadPending,
      uploadEntry: params?.uploadEntry,
      uploadFrom: params?.uploadFrom,
      plaidTrackingContext: params?.plaidTrackingContext,
    });
  });

  return {
    startFlow,
    startManageFlow,
    openAddAccount,
  };
};
